// src/constants/communityCatalog.js
// ============================================================================
// COMMUNITY GAME CATALOG (founder 2026-08-13) — the "giant list".
//
// Canonical entries for competitive/community games that do NOT have a full
// GAME_PROFILES entry, so a typed "Other" game lands on the right name and
// genre no matter how the creator spells or categorizes it ("Halo" filed
// under Fighting still creates a Shooter event named Halo Infinite).
//
// How it is used (constants/communityGames.js builds the lookup):
//   - Every entry's name and each alias are slugged with the SAME slugger as
//     communityGameKeyFromName; all of them resolve to ONE canonical key.
//   - Canonical key = slug of `name` (a cg_* key), or `key` when the entry
//     redirects to a browse-catalog extra ("mario kart" -> mkw).
//   - Catalog name + genre are CANONICAL: they override whatever the creator
//     typed or picked, same rule as the extras.
//
// Rules for entries:
//   - ⛔ NEVER include (or alias to) a GAME_PROFILES game — full-profile
//     games have their own formats/vision/verification, and the generic
//     community create form does not match them. `key` may ONLY name an
//     EXTRA_EVENT_GAMES key.
//   - Names are display-canonical (event cards show them verbatim).
//   - Aliases are free text; they get slugged at build time. Include common
//     shorthand, abbreviations, and prior-title names people actually type.
//   - genre: fighting | shooter | sports | racing | moba | rts | other.
//   - PvE/solo-only games are deliberately out — events need two sides or a
//     grid to settle.
//
// MIRROR: functions/constants/communityCatalog.js — keep in sync.
// ============================================================================

export const COMMUNITY_GAME_CATALOG = [
  // ── Redirects to browse-catalog extras (canonical entry lives there) ──────
  // ⚠️ Mario Kart 8 Deluxe is a DIFFERENT game on different hardware and has
  // its own entry below — only the generic 'mario kart' redirects here.
  { name: 'Mario Kart World', key: 'mkw', aliases: ['mario kart'] },
  { name: 'F1 25', key: 'f25', aliases: ['f1', 'formula 1', 'formula one'] },
  { name: 'EA Sports FC', key: 'efc', aliases: ['fifa', 'ea fc', 'fc 25', 'fc 26', 'ea sports fc 25', 'ea sports fc 26'] },
  { name: 'MLB The Show', key: 'mts', aliases: ['mlb the show 25', 'mlb the show 26', 'the show', 'mlb'] },
  { name: 'StarCraft 2', key: 'sc2', aliases: ['starcraft ii', 'sc2'] },
  { name: 'Warcraft 3', key: 'wc3', aliases: ['warcraft iii', 'warcraft 3 reforged', 'wc3'] },
  { name: 'Age of Empires 2', key: 'ae2', aliases: ['aoe2', 'age of empires ii', 'age of empires 2 definitive edition'] },
  { name: 'Total War: Warhammer 3', key: 'tw3', aliases: ['total war warhammer 3', 'total war warhammer iii', 'tww3'] },
  { name: 'World of Warcraft', key: 'wow', aliases: ['wow', 'wow classic', 'world of warcraft classic'] },
  { name: 'SMITE 2', key: 'sm2', aliases: ['smite'] },

  // ── Fighting ──────────────────────────────────────────────────────────────
  { name: 'Guilty Gear Strive', genre: 'fighting', aliases: ['guilty gear', 'ggst'] },
  { name: 'BlazBlue Central Fiction', genre: 'fighting', aliases: ['blazblue'] },
  { name: 'The King of Fighters XV', genre: 'fighting', aliases: ['kof xv', 'kof 15', 'king of fighters'] },
  { name: 'Fatal Fury: City of the Wolves', genre: 'fighting', aliases: ['fatal fury', 'cotw'] },
  { name: 'Melty Blood: Type Lumina', genre: 'fighting', aliases: ['melty blood'] },
  { name: 'Under Night In-Birth II', genre: 'fighting', aliases: ['under night', 'uni2', 'unib'] },
  { name: 'Granblue Fantasy Versus: Rising', genre: 'fighting', aliases: ['granblue', 'gbvsr', 'granblue fantasy versus'] },
  { name: 'Dragon Ball FighterZ', genre: 'fighting', aliases: ['dbfz', 'dragonball fighterz'] },
  { name: 'Skullgirls', genre: 'fighting', aliases: ['skullgirls 2nd encore'] },
  { name: 'Killer Instinct', genre: 'fighting', aliases: ['ki'] },
  { name: 'Injustice 2', genre: 'fighting', aliases: ['injustice'] },
  { name: 'Mortal Kombat 11', genre: 'fighting', aliases: ['mk11'] },
  { name: 'Tekken 7', genre: 'fighting', aliases: [] },
  { name: 'SoulCalibur VI', genre: 'fighting', aliases: ['soulcalibur', 'soul calibur 6'] },
  { name: 'Virtua Fighter 5 R.E.V.O.', genre: 'fighting', aliases: ['virtua fighter', 'vf5'] },
  { name: 'Super Smash Bros. Melee', genre: 'fighting', aliases: ['melee', 'smash melee'] },
  { name: 'Brawlhalla', genre: 'fighting', aliases: [] },
  { name: 'Rivals of Aether', genre: 'fighting', aliases: [] },
  { name: 'Rivals of Aether II', genre: 'fighting', aliases: ['rivals 2', 'rivals ii'] },
  { name: 'Persona 4 Arena Ultimax', genre: 'fighting', aliases: ['p4au', 'persona 4 arena'] },
  { name: "JoJo's Bizarre Adventure: All-Star Battle R", genre: 'fighting', aliases: ['jojo asbr', 'all star battle r'] },
  { name: 'Ultimate Marvel vs. Capcom 3', genre: 'fighting', aliases: ['umvc3', 'marvel vs capcom 3', 'mvc3'] },
  { name: 'Street Fighter III: 3rd Strike', genre: 'fighting', aliases: ['3rd strike', 'third strike'] },
  { name: 'Pokken Tournament DX', genre: 'fighting', aliases: ['pokken'] },
  { name: 'ARMS', genre: 'fighting', aliases: [] },
  { name: 'For Honor', genre: 'fighting', aliases: [] },

  // ── Shooters ──────────────────────────────────────────────────────────────
  { name: 'Halo Infinite', genre: 'shooter', aliases: ['halo'] },
  { name: 'Halo: The Master Chief Collection', genre: 'shooter', aliases: ['halo mcc', 'master chief collection', 'halo 3'] },
  { name: 'Fortnite', genre: 'shooter', aliases: ['fortnite battle royale'] },
  { name: 'PUBG: Battlegrounds', genre: 'shooter', aliases: ['pubg', 'playerunknowns battlegrounds'] },
  { name: 'Escape from Tarkov', genre: 'shooter', aliases: ['tarkov', 'eft'] },
  { name: 'Destiny 2', genre: 'shooter', aliases: ['destiny'] },
  { name: 'Battlefield 6', genre: 'shooter', aliases: ['battlefield', 'bf6'] },
  { name: 'Team Fortress 2', genre: 'shooter', aliases: ['tf2'] },
  { name: 'Quake Champions', genre: 'shooter', aliases: ['quake'] },
  { name: 'The Finals', genre: 'shooter', aliases: [] },
  { name: 'Splitgate 2', genre: 'shooter', aliases: ['splitgate'] },
  { name: 'Splatoon 3', genre: 'shooter', aliases: ['splatoon'] },
  { name: 'Gears 5', genre: 'shooter', aliases: ['gears of war'] },
  { name: 'Hunt: Showdown 1896', genre: 'shooter', aliases: ['hunt showdown'] },
  { name: 'Hell Let Loose', genre: 'shooter', aliases: [] },
  { name: 'Squad', genre: 'shooter', aliases: [] },
  { name: 'Insurgency: Sandstorm', genre: 'shooter', aliases: ['insurgency'] },
  { name: 'Arma Reforger', genre: 'shooter', aliases: ['arma'] },
  { name: 'DayZ', genre: 'shooter', aliases: [] },
  { name: 'Rust', genre: 'shooter', aliases: [] },
  { name: 'Delta Force', genre: 'shooter', aliases: ['delta force hawk ops'] },
  { name: 'FragPunk', genre: 'shooter', aliases: [] },
  { name: 'Strinova', genre: 'shooter', aliases: [] },
  { name: 'Mecha BREAK', genre: 'shooter', aliases: ['mecha break'] },
  { name: 'ARC Raiders', genre: 'shooter', aliases: [] },
  { name: 'Titanfall 2', genre: 'shooter', aliases: ['titanfall'] },
  { name: 'Star Wars Battlefront II', genre: 'shooter', aliases: ['battlefront 2', 'star wars battlefront'] },
  { name: 'Paladins', genre: 'shooter', aliases: [] },
  { name: 'Enlisted', genre: 'shooter', aliases: [] },
  { name: 'War Thunder', genre: 'shooter', aliases: [] },
  { name: 'World of Tanks', genre: 'shooter', aliases: ['wot'] },
  { name: 'World of Warships', genre: 'shooter', aliases: [] },
  { name: 'Warhammer 40,000: Space Marine 2', genre: 'shooter', aliases: ['space marine 2'] },
  { name: 'Free Fire', genre: 'shooter', aliases: ['garena free fire'] },
  { name: 'Call of Duty: Mobile', genre: 'shooter', aliases: ['cod mobile', 'codm'] },
  { name: 'PUBG Mobile', genre: 'shooter', aliases: [] },

  // ── Sports ────────────────────────────────────────────────────────────────
  { name: 'eFootball', genre: 'sports', aliases: ['pes', 'pro evolution soccer'] },
  { name: 'Rematch', genre: 'sports', aliases: [] },
  { name: 'NHL', genre: 'sports', aliases: ['nhl 25', 'nhl 26', 'ea sports nhl'] },
  { name: 'WWE 2K', genre: 'sports', aliases: ['wwe', 'wwe 2k24', 'wwe 2k25'] },
  { name: 'EA Sports UFC', genre: 'sports', aliases: ['ufc', 'ufc 5'] },
  { name: 'Undisputed', genre: 'sports', aliases: ['undisputed boxing'] },
  { name: 'EA Sports College Football', genre: 'sports', aliases: ['college football 25', 'college football 26', 'ncaa football', 'cfb'] },
  { name: 'PGA Tour 2K', genre: 'sports', aliases: ['pga 2k25', 'pga tour 2k25'] },
  { name: 'EA Sports PGA Tour', genre: 'sports', aliases: [] },
  { name: 'TopSpin 2K25', genre: 'sports', aliases: ['topspin'] },
  { name: 'Super Mega Baseball 4', genre: 'sports', aliases: ['super mega baseball'] },
  { name: 'Football Manager', genre: 'sports', aliases: ['fm24', 'fm25'] },
  { name: 'Blood Bowl 3', genre: 'sports', aliases: ['blood bowl'] },
  { name: 'Windjammers 2', genre: 'sports', aliases: ['windjammers'] },
  { name: 'Skate', genre: 'sports', aliases: ['ea skate'] },
  { name: "Tony Hawk's Pro Skater 3 + 4", genre: 'sports', aliases: ['thps', 'tony hawk'] },
  { name: 'Riders Republic', genre: 'sports', aliases: [] },
  { name: 'Mario Strikers: Battle League', genre: 'sports', aliases: ['mario strikers'] },
  { name: 'Mario Tennis Aces', genre: 'sports', aliases: ['mario tennis'] },
  { name: 'Mario Golf: Super Rush', genre: 'sports', aliases: ['mario golf'] },
  { name: 'Nintendo Switch Sports', genre: 'sports', aliases: ['switch sports'] },

  // ── Racing ────────────────────────────────────────────────────────────────
  { name: 'Mario Kart 8 Deluxe', genre: 'racing', aliases: ['mario kart 8', 'mk8', 'mk8dx'] },
  { name: 'Forza Motorsport', genre: 'racing', aliases: ['forza'] },
  { name: 'Forza Horizon 5', genre: 'racing', aliases: ['forza horizon'] },
  { name: 'Assetto Corsa', genre: 'racing', aliases: [] },
  { name: 'Assetto Corsa Competizione', genre: 'racing', aliases: ['acc'] },
  { name: 'Assetto Corsa EVO', genre: 'racing', aliases: ['ac evo'] },
  { name: 'rFactor 2', genre: 'racing', aliases: ['rfactor'] },
  { name: 'Automobilista 2', genre: 'racing', aliases: ['ams2'] },
  { name: 'Le Mans Ultimate', genre: 'racing', aliases: ['lmu'] },
  { name: 'RaceRoom Racing Experience', genre: 'racing', aliases: ['raceroom'] },
  { name: 'EA Sports WRC', genre: 'racing', aliases: ['wrc'] },
  { name: 'DiRT Rally 2.0', genre: 'racing', aliases: ['dirt rally'] },
  { name: 'Wreckfest', genre: 'racing', aliases: [] },
  { name: 'Wreckfest 2', genre: 'racing', aliases: [] },
  { name: 'Trackmania', genre: 'racing', aliases: ['trackmania 2020'] },
  { name: 'Crash Team Racing: Nitro-Fueled', genre: 'racing', aliases: ['crash team racing', 'ctr'] },
  { name: 'Sonic Racing: CrossWorlds', genre: 'racing', aliases: ['sonic racing'] },
  { name: 'Team Sonic Racing', genre: 'racing', aliases: [] },
  { name: 'Disney Speedstorm', genre: 'racing', aliases: ['speedstorm'] },
  { name: 'Hot Wheels Unleashed 2', genre: 'racing', aliases: ['hot wheels unleashed'] },
  { name: 'The Crew Motorfest', genre: 'racing', aliases: ['the crew'] },
  { name: 'Need for Speed Unbound', genre: 'racing', aliases: ['need for speed', 'nfs'] },
  { name: 'BeamNG.drive', genre: 'racing', aliases: ['beamng'] },
  { name: 'Rennsport', genre: 'racing', aliases: [] },
  { name: 'MotoGP', genre: 'racing', aliases: ['motogp 25'] },
  { name: 'NASCAR 25', genre: 'racing', aliases: ['nascar'] },
  { name: 'F-Zero 99', genre: 'racing', aliases: ['fzero 99', 'f zero'] },

  // ── MOBA ──────────────────────────────────────────────────────────────────
  { name: 'Heroes of the Storm', genre: 'moba', aliases: ['hots'] },
  { name: 'Pokemon Unite', genre: 'moba', aliases: [] },
  { name: 'Mobile Legends: Bang Bang', genre: 'moba', aliases: ['mobile legends', 'mlbb'] },
  { name: 'Arena of Valor', genre: 'moba', aliases: ['aov'] },
  { name: 'Honor of Kings', genre: 'moba', aliases: ['hok'] },
  { name: 'League of Legends: Wild Rift', genre: 'moba', aliases: ['wild rift'] },
  { name: 'Predecessor', genre: 'moba', aliases: ['paragon'] },
  { name: 'Supervive', genre: 'moba', aliases: [] },
  { name: 'Deadlock', genre: 'moba', aliases: ['valve deadlock'] },

  // ── Strategy (rts) ────────────────────────────────────────────────────────
  { name: 'StarCraft: Remastered', genre: 'rts', aliases: ['starcraft 1', 'brood war', 'broodwar'] },
  { name: 'Age of Empires IV', genre: 'rts', aliases: ['aoe4', 'age of empires 4'] },
  { name: 'Age of Empires III', genre: 'rts', aliases: ['aoe3', 'age of empires 3'] },
  { name: 'Age of Mythology: Retold', genre: 'rts', aliases: ['age of mythology', 'aom'] },
  { name: 'Stormgate', genre: 'rts', aliases: [] },
  { name: 'Beyond All Reason', genre: 'rts', aliases: ['bar'] },
  { name: 'Supreme Commander: Forged Alliance', genre: 'rts', aliases: ['forged alliance', 'faf', 'supreme commander'] },
  { name: 'Company of Heroes 3', genre: 'rts', aliases: ['coh3', 'company of heroes'] },
  { name: 'Command & Conquer Remastered', genre: 'rts', aliases: ['command and conquer', 'red alert'] },
  { name: 'Tempest Rising', genre: 'rts', aliases: [] },
  { name: 'Sins of a Solar Empire II', genre: 'rts', aliases: ['sins of a solar empire'] },
  { name: 'Northgard', genre: 'rts', aliases: [] },
  { name: 'Dune: Spice Wars', genre: 'rts', aliases: ['spice wars'] },
  { name: 'Mechabellum', genre: 'rts', aliases: [] },
  { name: 'Broken Arrow', genre: 'rts', aliases: [] },
  { name: 'WARNO', genre: 'rts', aliases: [] },
  { name: 'Steel Division 2', genre: 'rts', aliases: ['steel division'] },
  { name: 'Civilization VII', genre: 'rts', aliases: ['civ 7', 'civilization 7'] },
  { name: 'Civilization VI', genre: 'rts', aliases: ['civ 6', 'civilization 6'] },

  // ── Card / autobattler / Pokemon ─────────────────────────────────────────
  { name: 'Hearthstone', genre: 'other', aliases: ['hearthstone battlegrounds'] },
  { name: 'Magic: The Gathering Arena', genre: 'other', aliases: ['mtg arena', 'magic the gathering', 'mtg'] },
  { name: 'Yu-Gi-Oh! Master Duel', genre: 'other', aliases: ['master duel', 'yugioh'] },
  { name: 'Pokemon TCG Live', genre: 'other', aliases: ['pokemon tcg'] },
  { name: 'Pokemon TCG Pocket', genre: 'other', aliases: ['tcg pocket'] },
  { name: 'Legends of Runeterra', genre: 'other', aliases: ['runeterra', 'lor'] },
  { name: 'Marvel Snap', genre: 'other', aliases: ['snap'] },
  { name: 'Shadowverse: Worlds Beyond', genre: 'other', aliases: ['shadowverse'] },
  { name: 'Teamfight Tactics', genre: 'other', aliases: ['tft'] },
  { name: 'The Bazaar', genre: 'other', aliases: [] },
  { name: 'Chess', genre: 'other', aliases: ['chess com', 'lichess'] },
  { name: 'Pokemon Scarlet & Violet', genre: 'other', aliases: ['pokemon vgc', 'pokemon scarlet violet', 'pokemon sv'] },
  { name: 'Pokemon Showdown', genre: 'other', aliases: [] },
  { name: 'Pokemon GO', genre: 'other', aliases: ['go battle league'] },
  { name: 'Clash Royale', genre: 'other', aliases: [] },
  { name: 'Clash of Clans', genre: 'other', aliases: ['coc'] },
  { name: 'Brawl Stars', genre: 'other', aliases: [] },

  // ── Party / arcade / rhythm ──────────────────────────────────────────────
  { name: 'Fall Guys', genre: 'other', aliases: [] },
  { name: 'Stumble Guys', genre: 'other', aliases: [] },
  { name: 'Tetris 99', genre: 'other', aliases: [] },
  { name: 'TETR.IO', genre: 'other', aliases: ['tetrio'] },
  { name: 'Puyo Puyo Tetris 2', genre: 'other', aliases: ['puyo puyo tetris'] },
  { name: 'Tetris Effect: Connected', genre: 'other', aliases: ['tetris effect'] },
  { name: 'Golf With Your Friends', genre: 'other', aliases: [] },
  { name: 'Gang Beasts', genre: 'other', aliases: [] },
  { name: 'Party Animals', genre: 'other', aliases: [] },
  { name: 'Super Mario Party Jamboree', genre: 'other', aliases: ['mario party jamboree'] },
  { name: 'Mario Party Superstars', genre: 'other', aliases: ['mario party'] },
  { name: 'osu!', genre: 'other', aliases: ['osu'] },
  { name: 'Beat Saber', genre: 'other', aliases: [] },
  { name: 'Clone Hero', genre: 'other', aliases: [] },
  { name: 'Among Us', genre: 'other', aliases: [] },

  // ── MMO / survival / misc versus ─────────────────────────────────────────
  { name: 'Final Fantasy XIV', genre: 'other', aliases: ['ffxiv', 'ff14'] },
  { name: 'Guild Wars 2', genre: 'other', aliases: ['gw2'] },
  { name: 'The Elder Scrolls Online', genre: 'other', aliases: ['eso', 'elder scrolls online'] },
  { name: 'Old School RuneScape', genre: 'other', aliases: ['osrs'] },
  { name: 'RuneScape', genre: 'other', aliases: ['runescape 3', 'rs3'] },
  { name: 'Albion Online', genre: 'other', aliases: ['albion'] },
  { name: 'EVE Online', genre: 'other', aliases: ['eve'] },
  { name: 'New World: Aeternum', genre: 'other', aliases: ['new world'] },
  { name: 'Lost Ark', genre: 'other', aliases: [] },
  { name: 'Throne and Liberty', genre: 'other', aliases: [] },
  { name: 'Dune: Awakening', genre: 'other', aliases: ['dune awakening'] },
  { name: 'Minecraft', genre: 'other', aliases: ['minecraft java', 'minecraft bedrock'] },
  { name: 'ARK: Survival Ascended', genre: 'other', aliases: ['ark'] },
  { name: 'Palworld', genre: 'other', aliases: [] },
  { name: 'V Rising', genre: 'other', aliases: [] },
  { name: 'Sea of Thieves', genre: 'other', aliases: [] },
  { name: 'Dead by Daylight', genre: 'other', aliases: ['dbd'] },
  { name: 'Diablo IV', genre: 'other', aliases: ['diablo 4'] },
  { name: 'Path of Exile', genre: 'other', aliases: ['poe'] },
  { name: 'Path of Exile 2', genre: 'other', aliases: ['poe 2', 'poe2'] },
  { name: 'Naraka: Bladepoint', genre: 'other', aliases: ['naraka'] },
  { name: 'Chivalry 2', genre: 'other', aliases: ['chivalry'] },
  { name: 'Mordhau', genre: 'other', aliases: [] },
  { name: 'Farming Simulator', genre: 'other', aliases: ['farming simulator 25', 'fs25'] },
  { name: 'Roblox', genre: 'other', aliases: [] },
];

